import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Send, Upload, Copy, Trash2, Archive, ArchiveRestore, Search, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { errMsg } from "@/lib/api";
import { useApi, PageHead, ExportMenu } from "@/admin/AdminApp";

const STATUS = { attending: ["Attending", "bg-emerald-50 text-emerald-800"], declined: ["Declined", "bg-rose-50 text-rose-800"], pending: ["Not responded", "bg-[var(--ivory-2)] text-slate-600"] };
const linkFor = (f) => `${window.location.origin}/f/${f.link_token}`;

export const Confirm = ({ open, onOpenChange, title, text, action = "Confirm", onConfirm }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent className="bg-[var(--ivory)]" data-testid="confirm-dialog">
      <AlertDialogTitle className="font-serif-d text-2xl font-normal">{title}</AlertDialogTitle>
      <AlertDialogDescription>{text}</AlertDialogDescription>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="confirm-cancel-btn">Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className="bg-red-700 hover:bg-red-800" data-testid="confirm-ok-btn">{action}</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const AddFamily = ({ open, onOpenChange, onDone }) => {
  const api = useApi();
  const blank = { name: "", email: "", mobile: "", notes: "", send_invite: true };
  const [f, setF] = useState(blank);
  const save = async (e) => {
    e.preventDefault();
    try {
      const r = await api("post", "/families", f);
      if (r.invite_sent) toast.success(`Family added and invitation emailed to ${r.email}`);
      else if (f.send_invite && r.email) toast.error(`Family added, but the invitation email failed: ${r.invite_error}`);
      else toast.success("Family added");
      setF(blank);
      onDone();
    }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[var(--ivory)]" data-testid="add-family-dialog">
        <DialogTitle className="font-serif-d text-2xl font-normal">Add a family</DialogTitle>
        <form onSubmit={save} className="space-y-3">
          {[["name", "Family name *"], ["email", "Email"], ["mobile", "Mobile (with country code)"], ["notes", "Notes"]].map(([k, l]) => (
            <label key={k} className="block"><span className="label-lux">{l}</span>
              <input className="input-lux" required={k === "name"} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`add-family-${k}-input`} /></label>
          ))}
          <label className="flex items-center gap-3 text-sm"><Checkbox checked={f.send_invite} onCheckedChange={(v) => setF({ ...f, send_invite: !!v })} data-testid="add-family-send-invite-checkbox" /> Email the invitation link now</label>
          <button className="btn-ink w-full" data-testid="add-family-submit-btn">Add family</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ImportDialog = ({ open, onOpenChange, onDone }) => {
  const api = useApi();
  const [file, setFile] = useState(null);
  const [send, setSend] = useState(false);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const go = async () => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("send_invites", send ? "true" : "false");
    setBusy(true);
    try { const r = await api("post", "/import", fd); setRes(r); onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); setRes(null); setFile(null); }}>
      <DialogContent className="bg-[var(--ivory)]" data-testid="import-dialog">
        <DialogTitle className="font-serif-d text-2xl font-normal">Import guest list</DialogTitle>
        <p className="text-sm text-slate-600">CSV or Excel with columns: <b>Family Name</b>, <b>Email</b>, Mobile (optional), Notes (optional). Codes are generated automatically; families with an email already on the list are skipped.</p>
        <input type="file" accept=".csv,.xlsx" onChange={(e) => setFile(e.target.files[0])} className="text-sm" data-testid="import-file-input" />
        <label className="flex items-center gap-3 text-sm"><Checkbox checked={send} onCheckedChange={(v) => setSend(!!v)} data-testid="import-send-invites-checkbox" /> Email each family their invitation now</label>
        <button className="btn-ink" disabled={!file || busy} onClick={go} data-testid="import-submit-btn">{busy && <Loader2 size={15} className="animate-spin" />} Import</button>
        {res && (
          <div className="rounded-xl bg-white p-4 text-sm" data-testid="import-result">
            Created {res.created} · skipped {res.skipped} · invitations sent {res.sent}
            {res.errors.map((e) => <div key={e} className="mt-1 text-red-700">{e}</div>)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function Families() {
  const api = useApi();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [view, setView] = useState("active");
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState([]);
  const [dlg, setDlg] = useState("");
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(() => api("get", `/families?view=${view}`).then(setData).catch((e) => toast.error(errMsg(e))), [api, view]);
  useEffect(() => { load(); setSel([]); }, [load]);

  const list = useMemo(() => (data?.families || []).filter((f) => {
    if (filter === "incomplete" ? !f.incomplete : filter !== "all" && f.rsvp !== filter) return false;
    const s = q.toLowerCase();
    return !s || [f.name, f.email, f.mobile, f.code, f.person_names].join(" ").toLowerCase().includes(s);
  }), [data, filter, q]);

  const inviteNew = async () => {
    try { const r = await api("post", "/families/invite-new"); toast.success(`Sent ${r.sent} of ${r.total} invitations`); r.failed.forEach((f) => toast.error(f)); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const toggleOpen = async (v) => {
    try { await api("put", "/settings", { registrations_open: v }); setData({ ...data, registrations_open: v }); } catch (e) { toast.error(errMsg(e)); }
  };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); setSel([]); load(); } catch (e) { toast.error(errMsg(e)); } };

  if (!data) return <Loader2 className="mx-auto animate-spin text-[var(--gold)]" />;
  const st = data.stats;
  return (
    <div data-testid="families-page">
      <PageHead title="Family & friends" text="Import your guest list or add families one by one. Every family gets an invitation code and private link automatically.">
        <label className="btn-ghost !py-2 text-sm">Registrations open <Switch checked={data.registrations_open} onCheckedChange={toggleOpen} data-testid="registrations-open-switch" /></label>
        <ExportMenu path="/export" name="guest-list" testId="export-btn" />
        <button className="btn-ghost !py-2 text-sm" onClick={() => setConfirm({ title: "Send invitations?", text: "Every family with an email that hasn't been invited yet will receive the invitation email.", action: "Send", fn: inviteNew })} data-testid="invite-new-btn"><Send size={15} /> Invite new</button>
        <button className="btn-ghost !py-2 text-sm" onClick={() => setDlg("import")} data-testid="import-btn"><Upload size={15} /> Import</button>
        <button className="btn-ink !py-2 text-sm" onClick={() => setDlg("add")} data-testid="add-family-btn"><Plus size={15} /> Family</button>
      </PageHead>

      <div className="mb-8 grid grid-cols-2 gap-3 md:grid-cols-5">
        {[["Families", st.families], ["Attending", st.attending], ["Declining", st.declined], ["Not responded", st.pending], ["People registered", st.people]].map(([l, v]) => (
          <div key={l} className="rounded-2xl border border-[var(--line)] bg-white p-5" data-testid={`stat-${l.split(" ")[0].toLowerCase()}`}>
            <div className="eyebrow">{l}</div><div className="font-serif-d mt-2 text-4xl">{v}</div>
          </div>
        ))}
      </div>

      <div className="mb-4 flex flex-col gap-3 lg:flex-row">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="input-lux !rounded-full !pl-11" placeholder="Search families or guests" value={q} onChange={(e) => setQ(e.target.value)} data-testid="families-search-input" />
        </div>
        <div className="flex gap-1 overflow-x-auto rounded-full border border-[var(--line)] bg-white p-1">
          {[["all", "All"], ["attending", "Attending"], ["declined", "Declined"], ["pending", "No reply"], ["incomplete", "Incomplete"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`} className={`shrink-0 rounded-full px-4 py-1.5 text-sm ${filter === k ? "bg-[var(--ink)] text-[var(--ivory)]" : "text-slate-600"}`}>{l}</button>
          ))}
        </div>
        <div className="flex gap-1 rounded-full border border-[var(--line)] bg-white p-1">
          {["active", "archived"].map((v) => <button key={v} onClick={() => setView(v)} data-testid={`view-${v}`} className={`rounded-full px-4 py-1.5 text-sm capitalize ${view === v ? "bg-[var(--gold)] text-white" : "text-slate-600"}`}>{v}</button>)}
        </div>
      </div>

      {sel.length > 0 && (
        <div className="mb-3 flex items-center justify-between rounded-2xl bg-[var(--ink)] px-5 py-3 text-[var(--ivory)]" data-testid="bulk-bar">
          <span className="text-sm">{sel.length} selected</span>
          <button className="flex items-center gap-2 rounded-full bg-red-700 px-4 py-1.5 text-sm" data-testid="bulk-delete-btn"
            onClick={() => setConfirm({ title: `Delete ${sel.length} families?`, text: "They will be removed from the list and their invitation codes will stop working.", action: "Delete", fn: () => act(() => api("post", "/families/bulk-delete", { ids: sel }), "Families deleted") })}>
            <Trash2 size={14} /> Delete selected
          </button>
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-[var(--line)] bg-white">
        <table className="w-full min-w-[900px] text-left text-sm" data-testid="families-table">
          <thead>
            <tr className="border-b border-[var(--line)] text-xs uppercase tracking-[.16em] text-slate-500">
              <th className="w-12 px-5 py-3"><Checkbox checked={list.length > 0 && sel.length === list.length} onCheckedChange={(v) => setSel(v ? list.map((f) => f.id) : [])} data-testid="select-all-checkbox" /></th>
              <th className="px-3 py-3 font-normal">Name</th><th className="px-3 py-3 font-normal">Email</th><th className="px-3 py-3 font-normal">Phone</th>
              <th className="px-3 py-3 font-normal">Code</th><th className="px-3 py-3 font-normal">Status</th><th className="px-3 py-3 text-center font-normal">People</th><th className="px-3 py-3" />
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={8} className="p-10 text-center text-slate-500" data-testid="families-empty">No families here yet.</td></tr>}
            {list.map((f) => (
              <tr key={f.id} className="border-b border-[var(--line)] align-middle last:border-0 hover:bg-[#FFFDF8]" data-testid={`family-row-${f.code}`}>
                <td className="px-5 py-3"><Checkbox checked={sel.includes(f.id)} onCheckedChange={(v) => setSel(v ? [...sel, f.id] : sel.filter((x) => x !== f.id))} data-testid={`family-checkbox-${f.code}`} /></td>
                <td className="px-3 py-3">
                  <button className="text-left" onClick={() => nav(`/admin/families/${f.id}`)} data-testid={`family-open-${f.code}`}>
                    <span className="text-[15px] font-medium hover:underline">{f.name}</span>
                    {f.incomplete && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800" data-testid={`incomplete-badge-${f.code}`}><AlertTriangle size={11} /> Incomplete</span>}
                    <div className="text-xs text-slate-400">{f.invited_at ? `Invited ${new Date(f.invited_at).toLocaleDateString()}` : "Not invited yet"}</div>
                  </button>
                </td>
                <td className="px-3 py-3 text-slate-600" data-testid={`family-email-${f.code}`}>{f.email || "—"}</td>
                <td className="whitespace-nowrap px-3 py-3 text-slate-600" data-testid={`family-phone-${f.code}`}>{f.mobile || "—"}</td>
                <td className="px-3 py-3 font-mono tracking-wider" data-testid={`family-code-${f.code}`}>{f.code}</td>
                <td className="px-3 py-3"><span className={`whitespace-nowrap rounded-full px-3 py-1 text-xs ${STATUS[f.rsvp]?.[1]}`} data-testid={`family-status-${f.code}`}>{STATUS[f.rsvp]?.[0]}</span></td>
                <td className="px-3 py-3 text-center" data-testid={`family-people-${f.code}`}>{f.people}</td>
                <td className="px-3 py-3">
                  <div className="flex justify-end gap-1">
              <button className="btn-ghost !px-3 !py-1.5 text-xs" onClick={() => { navigator.clipboard.writeText(linkFor(f)); toast.success("Private link copied"); }} data-testid={`copy-link-${f.code}`}><Copy size={13} /> Link</button>
              <button className="rounded-full p-2 text-slate-500 hover:bg-[var(--ivory-2)]" title={view === "archived" ? "Restore" : "Archive"} data-testid={`archive-${f.code}`}
                onClick={() => act(() => api("post", `/families/${f.id}/archive`, { value: view !== "archived" }), view === "archived" ? "Restored" : "Archived")}>
                {view === "archived" ? <ArchiveRestore size={16} /> : <Archive size={16} />}
              </button>
              <button className="rounded-full p-2 text-red-700 hover:bg-red-50" data-testid={`delete-${f.code}`}
                onClick={() => setConfirm({ title: `Delete ${f.name}?`, text: "The family is removed from the list and its invitation code stops working.", action: "Delete", fn: () => act(() => api("delete", `/families/${f.id}`), "Family deleted") })}>
                <Trash2 size={16} />
              </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AddFamily open={dlg === "add"} onOpenChange={(o) => setDlg(o ? "add" : "")} onDone={() => { setDlg(""); load(); }} />
      <ImportDialog open={dlg === "import"} onOpenChange={(o) => setDlg(o ? "import" : "")} onDone={load} />
      <Confirm open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)} {...(confirm || {})} onConfirm={() => { confirm.fn(); setConfirm(null); }} />
    </div>
  );
}
